import { logger } from "@/lib/logger"
import type {
  AlertConditionResource as BackendAlertConditionResource,
  AlertResource as BackendAlertResource,
  AlertEmailChannelConfig as BackendEmailChannelConfig,
  AlertHumanReviewChannelConfig as BackendHumanReviewChannelConfig,
  AlertSlackChannelConfig as BackendSlackChannelConfig,
  AlertWebhookChannelConfig as BackendWebhookChannelConfig,
} from "@/types_api/backend/transport/list_alerts_response"
import {
  normalizeConversationSourceFilter,
  type ConversationSourceFilter,
} from "@/types/conversationSource"

const log = logger("AlertTypes")

type NormalizeNullableFields<T> = {
  [K in keyof T]?: Exclude<T[K], null>
}

// --- Enums (const objects + types) ---

export const AlertScope = {
  ALL: "all",
  MONITORING: "uploaded",
  SIMULATION: "simulated",
} as const
export type AlertScope = ConversationSourceFilter

export const AlertScopeLabels: Record<AlertScope, string> = {
  all: "All",
  uploaded: "Uploaded",
  simulated: "Simulated",
}

export const MatchMode = {
  ALL: "ALL",
  ANY: "ANY",
} as const
export type MatchMode = (typeof MatchMode)[keyof typeof MatchMode]

export const MatchModeLabels: Record<MatchMode, string> = {
  ALL: "All conditions (AND)",
  ANY: "Any condition (OR)",
}

export const Aggregation = {
  SINGLE: "SINGLE",
  RUN_AVERAGE: "RUN_AVERAGE",
  RUN_FRACTION: "RUN_FRACTION",
  JOB_SUCCESS: "JOB_SUCCESS",
  BASELINE_DEVIATION: "BASELINE_DEVIATION",
} as const
export type Aggregation = (typeof Aggregation)[keyof typeof Aggregation]

export const AggregationLabels: Record<Aggregation, string> = {
  SINGLE: "Single Value",
  RUN_AVERAGE: "Run Average",
  RUN_FRACTION: "Run Fraction",
  JOB_SUCCESS: "Job Success",
  BASELINE_DEVIATION: "Baseline Deviation",
}

export const Operator = {
  GT: "GT",
  GTE: "GTE",
  LT: "LT",
  LTE: "LTE",
  EQ: "EQ",
  NEQ: "NEQ",
} as const
export type Operator = (typeof Operator)[keyof typeof Operator]

export const OperatorLabels: Record<Operator, string> = {
  GT: ">",
  GTE: ">=",
  LT: "<",
  LTE: "<=",
  EQ: "=",
  NEQ: "!=",
}

export const ChannelType = {
  SLACK: "SLACK",
  EMAIL: "EMAIL",
  WEBHOOK: "WEBHOOK",
  HUMAN_REVIEW: "HUMAN_REVIEW",
} as const
export type ChannelType = (typeof ChannelType)[keyof typeof ChannelType]

export const ChannelTypeLabels: Record<ChannelType, string> = {
  SLACK: "Slack",
  EMAIL: "Email",
  WEBHOOK: "Webhook",
  HUMAN_REVIEW: "Human Review",
}

export const EventOutcome = {
  TRIGGERED: "TRIGGERED",
  NOT_MET: "NOT_MET",
  SUPPRESSED: "SUPPRESSED",
  ERROR: "ERROR",
} as const
export type EventOutcome = (typeof EventOutcome)[keyof typeof EventOutcome]

export const EventOutcomeLabels: Record<EventOutcome, string> = {
  TRIGGERED: "Triggered",
  NOT_MET: "Not Met",
  SUPPRESSED: "Suppressed",
  ERROR: "Error",
}

export const StringMatchMode = {
  EXACT: "exact",
  CONTAINS: "contains",
  CONTAINS_CASE_INSENSITIVE: "contains_case_insensitive",
} as const
export type StringMatchMode =
  (typeof StringMatchMode)[keyof typeof StringMatchMode]

// --- Interfaces ---

export interface AlertCondition {
  metric_id?: string
  aggregation: Aggregation
  operator: Operator
  threshold_float?: number
  threshold_string?: string
  match_value?: string
  match_mode?: string | null
  baseline_id?: string
  sigma_threshold?: number
  direction?: "above" | "below" | "both"
  warm_up_count?: number
  window_size_runs?: number
  window_size_days?: number
}

export type SlackAlertChannelConfig =
  NormalizeNullableFields<BackendSlackChannelConfig>

export type EmailAlertChannelConfig =
  NormalizeNullableFields<BackendEmailChannelConfig>

export interface WebhookAlertChannelConfig extends Omit<
  NormalizeNullableFields<BackendWebhookChannelConfig>,
  "method"
> {
  method?: "POST" | "GET" | "PUT" | "PATCH"
}

export type HumanReviewAlertChannelConfig =
  NormalizeNullableFields<BackendHumanReviewChannelConfig>

export interface SlackAlertChannel {
  channel_type: "SLACK"
  config: SlackAlertChannelConfig
}

export interface EmailAlertChannel {
  channel_type: "EMAIL"
  config: EmailAlertChannelConfig
}

export interface WebhookAlertChannel {
  channel_type: "WEBHOOK"
  config: WebhookAlertChannelConfig
}

export interface HumanReviewAlertChannel {
  channel_type: "HUMAN_REVIEW"
  config: HumanReviewAlertChannelConfig
}

export type AlertChannel =
  | SlackAlertChannel
  | EmailAlertChannel
  | WebhookAlertChannel
  | HumanReviewAlertChannel

export interface Alert {
  id: string
  organization_id: string
  name: string
  description?: string
  scope: AlertScope
  match_mode: MatchMode
  conditions: AlertCondition[]
  channels: AlertChannel[]
  cooldown_seconds: number
  custom_message_template?: string
  agent_ids: string[]
  required_tags: string[]
  scheduled_run_ids: string[]
  customer_metadata: Record<string, string>
  enabled: boolean
  status?: BackendAlertResource["status"]
  trigger_count: number
  last_triggered_at?: string
  created_at: string
  updated_at: string
}

export interface ConditionResult {
  metric_id: string
  metric_display_name?: string
  aggregation: Aggregation
  operator: Operator
  threshold?: number | string | null
  computed_value?: number | string | null
  met: boolean
}

export interface DispatchResult {
  channel_type: ChannelType
  channel_summary?: string
  success: boolean
  error?: string | null
}

export interface AlertEvent {
  ulid: string
  alert_ulid: string
  outcome: EventOutcome
  run_id?: string
  condition_results: ConditionResult[]
  dispatched_channels: DispatchResult[]
  message_sent?: string | null
  created_at: string
}

interface RawAlertChannel {
  channel_type?: string
  config?: Record<string, unknown>
}

type RawAlertCondition = BackendAlertConditionResource

interface RawAlert extends Partial<
  Omit<BackendAlertResource, "conditions" | "channels">
> {
  id?: string
  alert_id?: string
  organization_id?: string
  enabled?: boolean
  created_at?: string
  updated_at?: string
  channels?: unknown[]
  conditions?: unknown[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value)
}

function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

function asNonBlankString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? value
    : undefined
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined
}

function asNullableString(value: unknown): string | null | undefined {
  return value === null ? null : asString(value)
}

function asStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === "string")
    ? value
    : undefined
}

function asStringRecord(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined
  }

  const entries = Object.entries(value)
  if (entries.every(([, item]) => typeof item === "string")) {
    return Object.fromEntries(entries) as Record<string, string>
  }

  return undefined
}

function asCustomerMetadata(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  )
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function asAggregation(value: unknown): Aggregation | undefined {
  return value === "SINGLE" ||
    value === "RUN_AVERAGE" ||
    value === "RUN_FRACTION" ||
    value === "JOB_SUCCESS" ||
    value === "BASELINE_DEVIATION"
    ? value
    : undefined
}

function asOperator(value: unknown): Operator | undefined {
  return value === "GT" ||
    value === "GTE" ||
    value === "LT" ||
    value === "LTE" ||
    value === "EQ" ||
    value === "NEQ"
    ? value
    : undefined
}

function asBaselineDeviationDirection(
  value: unknown,
): "above" | "below" | "both" | undefined {
  return value === "above" || value === "below" || value === "both"
    ? value
    : undefined
}

function asChannelType(value: unknown): ChannelType | undefined {
  return value === "SLACK" ||
    value === "EMAIL" ||
    value === "WEBHOOK" ||
    value === "HUMAN_REVIEW"
    ? value
    : undefined
}

function isRawAlertChannel(value: unknown): value is RawAlertChannel {
  return isRecord(value)
}

function isRawAlertCondition(value: unknown): value is RawAlertCondition {
  return isRecord(value)
}

function normalizeAlertChannel(
  channel: RawAlertChannel,
): AlertChannel | undefined {
  const channel_type = asChannelType(channel.channel_type)
  const config = asRecord(channel.config)

  switch (channel_type) {
    case "SLACK":
      return {
        channel_type,
        config: {
          channel_id: asNonBlankString(config.channel_id),
          channel_name: asNonBlankString(config.channel_name),
        },
      }
    case "EMAIL":
      return {
        channel_type,
        config: {
          recipients: asStringArray(config.recipients),
        },
      }
    case "HUMAN_REVIEW":
      return {
        channel_type,
        config: {
          project_id: asNonBlankString(config.project_id),
          sample_rate: asNumber(config.sample_rate),
        },
      }
    case "WEBHOOK":
      return {
        channel_type: "WEBHOOK",
        config: {
          url: asNonBlankString(config.url),
          method:
            config.method === "POST" ||
            config.method === "GET" ||
            config.method === "PUT" ||
            config.method === "PATCH"
              ? config.method
              : undefined,
          auth_token: asNonBlankString(config.auth_token),
          headers: asStringRecord(config.headers),
        },
      }
    case undefined:
    default: {
      const rawType = channel.channel_type
      if (!rawType) {
        return undefined
      }
      log.warn("Ignoring unknown alert channel type", { rawType })
      return undefined
    }
  }
}

export function normalizeAlert(rawAlert: unknown): Alert | undefined {
  if (!isRecord(rawAlert)) {
    return undefined
  }

  const alert = rawAlert as RawAlert

  const conditions = (alert.conditions ?? [])
    .filter(isRawAlertCondition)
    .map((condition) => {
      const thresholdString = asString(condition.threshold_string)
      return {
        metric_id: asString(condition.metric_id),
        aggregation: asAggregation(condition.aggregation) ?? Aggregation.SINGLE,
        operator: asOperator(condition.operator) ?? Operator.GT,
        threshold_float: asNumber(condition.threshold_float),
        threshold_string: thresholdString,
        match_value: asString(condition.match_value) ?? thresholdString,
        match_mode: asNullableString(condition.match_mode),
        baseline_id: asString(condition.baseline_id),
        sigma_threshold: asNumber(condition.sigma_threshold),
        direction: asBaselineDeviationDirection(condition.direction),
        warm_up_count: asNumber(condition.warm_up_count),
        window_size_runs: asNumber(condition.window_size_runs),
        window_size_days: asNumber(condition.window_size_days),
      }
    })

  const channels = (alert.channels ?? [])
    .filter(isRawAlertChannel)
    .map(normalizeAlertChannel)
    .filter((channel): channel is AlertChannel => channel != null)

  return {
    id: alert.id ?? alert.ulid ?? alert.alert_id ?? "",
    organization_id: alert.organization_id ?? "",
    name: alert.name ?? "",
    description: alert.description,
    scope:
      normalizeConversationSourceFilter(
        alert.conversation_source ?? alert.scope,
      ) ?? AlertScope.ALL,
    match_mode: alert.match_mode ?? MatchMode.ALL,
    conditions,
    channels,
    cooldown_seconds: alert.cooldown_seconds ?? 0,
    custom_message_template: asString(alert.custom_message_template),
    agent_ids: asStringArray(alert.agent_ids) ?? [],
    required_tags: asStringArray(alert.required_tags) ?? [],
    scheduled_run_ids: asStringArray(alert.scheduled_run_ids) ?? [],
    customer_metadata: asCustomerMetadata(alert.customer_metadata),
    enabled: alert.enabled ?? alert.status === "ACTIVE",
    status: alert.status,
    trigger_count: alert.trigger_count ?? 0,
    last_triggered_at: asString(alert.last_triggered_at),
    created_at: alert.created_at ?? alert.create_time ?? "",
    updated_at: alert.updated_at ?? alert.update_time ?? "",
  }
}
